import type { ReactElement, ReactNode } from "react"
import styled from "styled-components"
import { usePocOptions } from "~/features/poc"
import { Provenance } from "~/features/provenance"
import { ministryLabel } from "~/shared/theme/audience"
import { type SectionStyle, toneFor } from "~/shared/theme/tokens"
import { Card, ManageAccess, RemoteImage } from "~/shared/ui"
import type { EntitlementItem } from "../types"
import { AccessBadge } from "./AccessBadge"

const Row = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`

const CardLink = styled.a`
  display: block;
  text-decoration: none;
  border-radius: ${({ theme }) => theme.radius.md};

  &:hover h3 {
    color: ${({ theme }) => theme.color.linkHover};
    text-decoration: underline;
  }
`

/**
 * A header icon, padded to a comfortable hit target. A sibling of the card
 * link, never a descendant: the card body is already one anchor, and nesting a
 * second inside it is invalid HTML that browsers repair unpredictably.
 */
const IconLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xxs};
  color: inherit;

  &:hover {
    opacity: 0.8;
  }
`

interface EntitlementCardProps {
  readonly item: EntitlementItem
  /** How the section draws its cards: the artwork well, and the tone an unplaced card wears. */
  readonly section: SectionStyle
  readonly qaHook: string
}

/**
 * The card itself is the affordance — clicking the artwork goes to the content,
 * matching the Studies and Ebooks cards rather than parking a separate Access
 * button beside them.
 *
 * The target is either the row's own URL (memberships carry one) or myLifeway's
 * redirect page, which resolves `/resources/{itemNumber}/locations` itself. That
 * keeps `accessTargets` — one commerce call per row — out of the list query
 * (proposal 03 §4).
 *
 * Outside the view window the card is not a link at all: MLC renders its button
 * disabled and relabelled (`utils/tableCellHelpers.js#resourceLink`), and a card
 * with no link plus an `Expired` pill says the same thing without a dead click.
 */
export const EntitlementCard = ({ item, section, qaHook }: EntitlementCardProps): ReactElement => {
  /** POC. Off strips the tinted block and the header icons — see `Card`'s `plain`. */
  const { cardColor } = usePocOptions()

  const manageLink = item.window.manageHref ? (
    <IconLink
      href={item.window.manageHref}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Manage access for ${item.title}`}
      data-qa-hook={`${qaHook}-manage`}
    >
      <ManageAccess />
    </IconLink>
  ) : null

  const isReachable = Boolean(item.accessHref) && item.window.state === "active"

  const bodyLink = isReachable
    ? (linkBody: ReactNode): ReactElement => (
        <CardLink
          href={item.accessHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${item.title} (opens on myLifeway)`}
          data-qa-hook={`${qaHook}-link`}
        >
          {linkBody}
        </CardLink>
      )
    : undefined

  return (
    <Row>
      {/* Row, artwork, window and target are all commerce's — nothing here touches the DCE graph. */}
      <Provenance source="mlc">
        <Card
          qaHook={qaHook}
          title={item.title}
          tone={toneFor(item.audience, section.tone)}
          plain={!cardColor}
          label={ministryLabel(item.audience)}
          action={manageLink ?? undefined}
          badge={<AccessBadge accessWindow={item.window} qaHook={`${qaHook}-badge`} />}
          media={
            <RemoteImage
              src={item.imageUrl}
              alt=""
              ratio={section.artwork.ratio}
              fit={section.artwork.fit}
              qaHook={`${qaHook}-image`}
            />
          }
          hasCoverArt={Boolean(item.imageUrl)}
          bodyLink={bodyLink}
        />
      </Provenance>
    </Row>
  )
}
